/* ==========================================================================
   NGA — AI predictive search

   Layers on one input:
     1. Animated rotating keyword placeholder (typewriter).
     2. A full-bleed results panel — suggested searches, live products with
        quick add, real category counts, and an editorial promo.
     3. A semantic intent layer that reads natural language ("what to wear
        golfing", "something pink", "skort") and routes it to the right
        collection — the part Shopify's own predictive search cannot do.

   Results come from /search?...&view=suggest, a JSON template in this theme.
   Shopify's /search/suggest.json caps at 10 per resource and returns no
   totals, so it cannot power "6 results" or "View all 14 results".

   No content lives in this file. Keywords, intent rules, suggestion
   templates and every visible string come from theme settings.

   Progressive enhancement: the <form> submits to /search with no JS at all.
   ========================================================================== */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var RECENT_KEY = 'nga:recent-searches';
  var RECENT_MAX = 6;
  var SIZE_TOKENS = ['xs', 's', 'm', 'l', 'xl', 'xxl', 'xs-s', 's-m', 'm-l'];

  function normalise(s) {
    return (s || '').toLowerCase().replace(/[^a-z0-9\s'-]/g, ' ').replace(/\s+/g, ' ').trim();
  }

  function detectIntent(query, rules) {
    if (!rules || !rules.length) return null;
    var q = normalise(query);
    if (q.length < 2) return null;

    var tokens = q.split(' ');
    var best = null;

    rules.forEach(function (rule) {
      if (!rule || !rule.any || !rule.any.length) return;
      var score = 0;
      rule.any.forEach(function (k) {
        if (!k) return;
        if (k.indexOf(' ') !== -1) {
          if (q.indexOf(k) !== -1) score += 4;
        } else if (tokens.indexOf(k) !== -1) {
          score += 3;
        } else if (k.length > 3 && q.indexOf(k) !== -1) {
          score += 1;
        }
      });
      if (!score) return;
      if (!best || score > best.score) best = { rule: rule, score: score };
    });

    if (!best || best.score < 3) return null;

    var size = null;
    tokens.forEach(function (t) { if (SIZE_TOKENS.indexOf(t) !== -1) size = t.toUpperCase(); });

    return {
      title: best.rule.title || best.rule.to,
      label: best.rule.label || '',
      url: best.rule.to,
      size: size
    };
  }

  function readRecent() {
    try { var r = JSON.parse(localStorage.getItem(RECENT_KEY)); return Array.isArray(r) ? r : []; }
    catch (e) { return []; }
  }
  function writeRecent(term) {
    if (!term) return;
    try {
      var list = readRecent().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
      list.unshift(term);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) { /* private mode */ }
  }
  function clearRecent() { try { localStorage.removeItem(RECENT_KEY); } catch (e) {} }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function highlight(text, query) {
    var safe = esc(text);
    var q = normalise(query);
    if (!q) return safe;
    var parts = q.split(' ').filter(function (t) { return t.length > 1; });
    if (!parts.length) return safe;
    try {
      var re = new RegExp('(' + parts.map(function (p) {
        return p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      }).join('|') + ')', 'gi');
      return safe.replace(re, '<mark>$1</mark>');
    } catch (e) { return safe; }
  }

  function money(cents) {
    if (cents == null) return '';
    var n = Number(cents);
    if (isNaN(n)) return '';
    if (window.Shopify && window.Shopify.formatMoney && window.NGA_MONEY_FORMAT) {
      try { return window.Shopify.formatMoney(n, window.NGA_MONEY_FORMAT); } catch (e) {}
    }
    return '$' + (n / 100).toFixed(2).replace(/\.00$/, '');
  }

  function titleCase(s) {
    return String(s || '').replace(/\w\S*/g, function (t) { return t[0].toUpperCase() + t.slice(1); });
  }

  var I = {
    search: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>',
    arrow: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12h15M13 6l6 6-6 6"/></svg>',
    sparkle: '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l1.7 5.5L19 9.2l-5.3 1.7L12 16.4l-1.7-5.5L5 9.2l5.3-1.7z"/></svg>',
    bag: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5.5 8h13l-1 12h-11z"/><path d="M9 8V6.5a3 3 0 0 1 6 0V8"/></svg>',
    heart: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20.5S3.5 15.4 3.5 9.6A4.6 4.6 0 0 1 12 7.1a4.6 4.6 0 0 1 8.5 2.5c0 5.8-8.5 10.9-8.5 10.9z"/></svg>',
    heartFull: '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 20.5S3.5 15.4 3.5 9.6A4.6 4.6 0 0 1 12 7.1a4.6 4.6 0 0 1 8.5 2.5c0 5.8-8.5 10.9-8.5 10.9z"/></svg>',
    star: '<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9z"/></svg>',
    starO: '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" aria-hidden="true"><path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9z"/></svg>',
    diamond: '<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.5l9.5 9.5L12 21.5 2.5 12z"/></svg>'
  };

  /* =====================================================================
     <nga-search>
     ===================================================================== */
  class NgaSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-nga-search-input]');
      this.form = this.querySelector('form');
      this.panel = this.querySelector('[data-nga-search-panel]');
      this.ghost = this.querySelector('[data-nga-ghost]');
      this.ghostWord = this.querySelector('[data-nga-ghost-word]');
      this.resetBtn = this.querySelector('[data-nga-search-reset]');
      this.micBtn = this.querySelector('[data-nga-search-mic]');
      this.escBtn = this.querySelector('[data-nga-search-esc]');

      if (!this.input || !this.panel) return;

      this.keywords = this.safeJSON('data-keywords', []);
      this.trending = this.safeJSON('data-trending', []);
      this.rules = this.safeJSON('data-rules', []);
      this.suggestTpl = this.safeJSON('data-suggest', []);
      this.promo = this.safeJSON('data-promo', null);
      this.t = this.safeJSON('data-strings', {});
      this.inline = this.hasAttribute('data-inline');

      this.controller = null;
      this.debounce = null;
      this.navIndex = -1;
      this.cache = {};

      this.bind();
      this.startTypewriter();
      this.renderIdle();
    }

    disconnectedCallback() {
      clearTimeout(this.typeTimer);
      clearTimeout(this.debounce);
      if (this.controller) this.controller.abort();
      document.removeEventListener('click', this.onDocClick, true);
      window.removeEventListener('resize', this.onResize);
    }

    safeJSON(attr, fallback) {
      var raw = this.getAttribute(attr);
      if (!raw) return fallback;
      try { var p = JSON.parse(raw); return p == null ? fallback : p; }
      catch (e) { return fallback; }
    }

    str(key, fallback) {
      var v = this.t && this.t[key];
      return (v == null || v === '') ? (fallback || '') : v;
    }

    /* Anchor the fixed panel directly under the header. */
    positionPanel() {
      if (this.inline) return;
      var header = this.closest('[data-nga-header]');
      var top = header ? header.getBoundingClientRect().bottom : 120;
      this.panel.style.setProperty('--nga-panel-top', Math.max(top, 0) + 'px');
    }

    bind() {
      var self = this;

      this.input.addEventListener('input', function () { self.onInput(); });
      this.input.addEventListener('focus', function () { self.open(); });
      this.input.addEventListener('keydown', function (e) { self.onKeydown(e); });

      if (this.form) {
        this.form.addEventListener('submit', function () { writeRecent(self.input.value.trim()); });
      }

      if (this.resetBtn) {
        this.resetBtn.addEventListener('click', function (e) {
          e.preventDefault();
          self.input.value = '';
          self.onInput();
          self.input.focus();
        });
      }

      if (this.escBtn) {
        this.escBtn.addEventListener('click', function (e) {
          e.preventDefault();
          self.close();
          self.input.blur();
        });
      }

      this.panel.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-nga-term]');
        if (chip) {
          e.preventDefault();
          self.input.value = chip.getAttribute('data-nga-term');
          self.onInput();
          self.input.focus();
          return;
        }
        if (e.target.closest('[data-nga-clear-recent]')) {
          e.preventDefault();
          clearRecent();
          self.renderIdle();
          return;
        }
        var quick = e.target.closest('[data-nga-search-add]');
        if (quick) { e.preventDefault(); self.quickAdd(quick); return; }
        if (e.target.closest('a[href]')) writeRecent(self.input.value.trim());
      });

      this.onDocClick = function (e) { if (!self.contains(e.target)) self.close(); };
      document.addEventListener('click', this.onDocClick, true);

      this.onResize = function () { if (self.classList.contains('is-open')) self.positionPanel(); };
      window.addEventListener('resize', this.onResize);
      window.addEventListener('scroll', this.onResize, { passive: true });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.classList.contains('is-open')) {
          self.close();
          self.input.blur();
        }
        if (
          e.key === '/' &&
          document.activeElement &&
          !/^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement.tagName) &&
          !document.activeElement.isContentEditable
        ) {
          e.preventDefault();
          self.input.focus();
        }
      });

      this.setupVoice();
    }

    /* ---------- Rotating keyword placeholder ---------- */
    startTypewriter() {
      if (!this.ghost || !this.ghostWord || !this.keywords.length) {
        if (this.ghost) this.ghost.hidden = true;
        return;
      }
      if (reduce.matches) { this.ghostWord.textContent = this.keywords[0]; return; }

      var self = this;
      var wordIndex = 0, charIndex = 0, deleting = false;

      function tick() {
        var word = self.keywords[wordIndex % self.keywords.length];
        if (!deleting) {
          charIndex++;
          self.ghostWord.textContent = word.slice(0, charIndex);
          if (charIndex === word.length) { deleting = true; self.typeTimer = setTimeout(tick, 1700); return; }
          self.typeTimer = setTimeout(tick, 62);
        } else {
          charIndex--;
          self.ghostWord.textContent = word.slice(0, charIndex);
          if (charIndex === 0) { deleting = false; wordIndex++; self.typeTimer = setTimeout(tick, 320); return; }
          self.typeTimer = setTimeout(tick, 28);
        }
      }
      this.typeTimer = setTimeout(tick, 700);
    }

    updateGhost() {
      if (!this.ghost) return;
      this.ghost.hidden = this.input.value.length > 0;
    }

    /* ---------- Voice ---------- */
    setupVoice() {
      var SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!this.micBtn) return;
      if (!SR) { this.micBtn.hidden = true; return; }

      var self = this;
      var rec = new SR();
      rec.lang = document.documentElement.lang || 'en-US';
      rec.interimResults = true;
      rec.continuous = false;

      rec.addEventListener('result', function (e) {
        var text = Array.prototype.slice.call(e.results).map(function (r) { return r[0].transcript; }).join('');
        self.input.value = text;
        self.onInput();
      });
      rec.addEventListener('end', function () { self.micBtn.classList.remove('is-listening'); });
      rec.addEventListener('error', function () { self.micBtn.classList.remove('is-listening'); });

      this.micBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (self.micBtn.classList.contains('is-listening')) { rec.stop(); return; }
        try { rec.start(); self.micBtn.classList.add('is-listening'); self.open(); }
        catch (err) { /* already running */ }
      });
    }

    /* ---------- Status for assistive tech ---------- */
    setStatus(text) {
      var el = this.querySelector('[data-nga-search-status]');
      if (el) el.textContent = text;
    }

    /* ---------- Open / close ---------- */
    open() {
      this.positionPanel();
      this.classList.add('is-open', 'is-focused');
      var header = this.closest('[data-nga-header]');
      if (header) header.classList.add('is-open');
      this.setStatus('Search suggestions are showing below.');
    }
    close() {
      this.classList.remove('is-open', 'is-focused');
      var header = this.closest('[data-nga-header]');
      if (header) header.classList.remove('is-open');
      this.setStatus('');
      this.navIndex = -1;
    }

    /* ---------- Input ---------- */
    onInput() {
      var q = this.input.value.trim();
      this.updateGhost();
      if (this.resetBtn) this.resetBtn.hidden = q.length === 0;
      this.navIndex = -1;
      clearTimeout(this.debounce);

      if (q.length === 0) { this.renderIdle(); this.open(); return; }
      if (q.length < 2) return;

      var self = this;
      this.debounce = setTimeout(function () { self.fetchResults(q); }, 200);
      this.open();
    }

    onKeydown(e) {
      var items = this.panel.querySelectorAll('[data-nga-nav]');
      if (!items.length) return;

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        var dir = e.key === 'ArrowDown' ? 1 : -1;
        this.navIndex = (this.navIndex + dir + items.length) % items.length;
        for (var i = 0; i < items.length; i++) items[i].classList.toggle('is-highlighted', i === this.navIndex);
        var active = items[this.navIndex];
        if (active) active.scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter') {
        var current = items[this.navIndex];
        if (current && current.tagName === 'A') {
          e.preventDefault();
          writeRecent(this.input.value.trim());
          window.location.href = current.href;
        }
      }
    }

    /* ---------- Quick add ---------- */
    quickAdd(btn) {
      var id = btn.getAttribute('data-nga-search-add');
      if (!id || id === '0') return;
      var original = btn.innerHTML;
      var addedLabel = this.str('added', 'Added');
      btn.disabled = true;
      btn.innerHTML = '…';

      var add = (window.NGA && window.NGA.addToCart)
        ? window.NGA.addToCart([{ id: Number(id), quantity: 1 }])
        : fetch(((window.routes && window.routes.cart_add_url) || '/cart/add') + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
          }).then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); });

      add.then(function () {
        btn.innerHTML = addedLabel;
        btn.classList.add('is-added');
        if (window.NGA && window.NGA.refreshCartCount) window.NGA.refreshCartCount();
        if (window.NGA && window.NGA.toast) window.NGA.toast('Added to your bag. <a href="/cart">View bag</a>', { variant: 'pink' });
        setTimeout(function () { btn.innerHTML = original; btn.classList.remove('is-added'); btn.disabled = false; }, 1800);
      }).catch(function () {
        btn.innerHTML = original;
        btn.disabled = false;
        if (window.NGA && window.NGA.toast) window.NGA.toast('Could not add to bag.');
      });
    }

    /* ---------- Fetch ---------- */
    fetchResults(q) {
      var self = this;
      if (this.cache[q]) { this.render(q, this.cache[q]); return; }

      if (this.controller) this.controller.abort();
      this.controller = ('AbortController' in window) ? new AbortController() : null;

      this.panel.innerHTML =
        '<div class="nga-search__loading"><span class="nga-search__spinner"></span><span>' +
        esc(this.str('loading', 'Searching…')) + '</span></div>';

      var url = '/search?q=' + encodeURIComponent(q) +
        '&type=product,collection,page,article&options[prefix]=last&view=suggest';

      fetch(url, this.controller ? { signal: this.controller.signal } : undefined)
        .then(function (r) { if (!r.ok) throw new Error('search failed'); return r.json(); })
        .then(function (data) { self.cache[q] = data; self.render(q, data); })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          self.panel.innerHTML =
            '<div class="nga-search__empty">' +
            '<p class="nga-search__empty-text">' + esc(self.str('error', 'We could not load live suggestions.')) + '</p>' +
            '<a class="nga-btn nga-btn--pink nga-btn--sm" href="/search?q=' + encodeURIComponent(q) + '">' +
            esc(self.str('searchFor', 'Search for')) + ' &ldquo;' + esc(q) + '&rdquo;</a></div>';
        });
    }

    /* ---------- Render: idle ---------- */
    renderIdle() {
      var recent = readRecent();
      var side = '';

      if (recent.length) {
        side += '<div class="nga-search__side-block"><div style="display:flex;align-items:center;justify-content:space-between;gap:1.2rem">' +
          '<h3 class="nga-search__side-title" style="margin:0">' + esc(this.str('recent', 'Recent')) + '</h3>' +
          '<button type="button" class="nga-search__group-clear" data-nga-clear-recent style="border:0;background:none;cursor:pointer;font-size:1.2rem;color:var(--nga-ink-mute);text-decoration:underline">' +
          esc(this.str('clear', 'Clear')) + '</button></div><ul class="nga-search__suggest">';
        recent.forEach(function (t) {
          side += '<li><a href="#" data-nga-term="' + esc(t) + '">' + I.search + '<span>' + esc(t) + '</span></a></li>';
        });
        side += '</ul></div>';
      }

      if (this.trending.length) {
        side += '<div class="nga-search__side-block"><h3 class="nga-search__side-title">' +
          esc(this.str('trending', 'Trending now')) + '</h3><div class="nga-search__chips">';
        this.trending.forEach(function (t) {
          var term = typeof t === 'string' ? t : t.term;
          side += '<button type="button" class="nga-search__chip" data-nga-term="' + esc(term) + '">' + esc(titleCase(term)) + '</button>';
        });
        side += '</div></div>';
      }

      this.panel.innerHTML =
        '<div class="nga-search__grid">' +
          '<div class="nga-search__side">' + side + '</div>' +
          '<div class="nga-search__main">' +
            '<div class="nga-search__empty" style="padding-top:var(--nga-s-6)">' +
              '<p class="nga-search__empty-text">' + esc(this.str('idleHelp', 'Start typing to search the collection.')) + '</p>' +
            '</div>' +
          '</div>' +
          this.promoHTML() +
        '</div>';
    }

    promoHTML() {
      var p = this.promo;
      if (!p || (!p.image && !p.title)) return '<div class="nga-search__promo"></div>';
      return '<div class="nga-search__promo">' +
        '<a class="nga-search__promo-card" href="' + esc(p.url || '#') + '">' +
          (p.image
            ? '<span class="nga-search__promo-media"><img src="' + esc(p.image) + '" alt="" loading="lazy" decoding="async"></span>'
            : '') +
          '<span class="nga-search__promo-title">' + esc(p.title || '') + '</span>' +
          (p.sub ? '<span class="nga-search__promo-sub">' + esc(p.sub) + '</span>' : '') +
          (p.cta ? '<span class="nga-search__promo-cta">' + esc(p.cta) + I.arrow + '</span>' : '') +
        '</a></div>';
    }

    /* ---------- Render: results ---------- */
    render(q, data) {
      var self = this;
      var products = data.products || [];
      var cats = data.categories || [];
      var total = data.total || 0;
      var intent = detectIntent(q, this.rules);

      /* ---- Left rail ---- */
      var side = '';

      // Suggested searches, expanded from settings templates.
      var suggestions = [];
      (this.suggestTpl || []).forEach(function (tpl) {
        var s = String(tpl).replace(/\{q\}/gi, q).trim();
        if (s && suggestions.indexOf(s) === -1) suggestions.push(s);
      });
      if (!suggestions.length) suggestions.push(q);

      side += '<div class="nga-search__side-block"><h3 class="nga-search__side-title">' +
        esc(this.str('suggested', 'Suggested searches')) + '</h3><ul class="nga-search__suggest">';
      suggestions.slice(0, 5).forEach(function (s) {
        side += '<li><a data-nga-nav href="/search?q=' + encodeURIComponent(s) + '">' +
          I.search + '<span>' + esc(titleCase(s)) + '</span></a></li>';
      });
      side += '</ul></div>';

      if (this.trending.length) {
        side += '<div class="nga-search__side-block"><h3 class="nga-search__side-title">' +
          esc(this.str('trending', 'Trending now')) + '</h3><div class="nga-search__chips">';
        this.trending.slice(0, 4).forEach(function (t) {
          var term = typeof t === 'string' ? t : t.term;
          side += '<button type="button" class="nga-search__chip" data-nga-term="' + esc(term) + '">' + esc(titleCase(term)) + '</button>';
        });
        side += '</div></div>';
      }

      if (cats.length) {
        side += '<div class="nga-search__side-block"><h3 class="nga-search__side-title">' +
          esc(this.str('categories', 'Categories')) + '</h3><ul class="nga-search__cats">';
        cats.slice(0, 6).forEach(function (c) {
          side += '<li><a data-nga-nav href="/search?q=' + encodeURIComponent(q + ' ' + c.name) + '">' +
            '<span>' + esc(c.name) + '</span><span>' + c.count + '</span></a></li>';
        });
        side += '</ul></div>';
      }

      /* ---- Middle ---- */
      var main = '';

      if (intent) {
        var href = intent.url;
        if (intent.size && href.indexOf('/collections/') === 0) {
          href += (href.indexOf('?') === -1 ? '?' : '&') + 'filter.v.option.size=' + encodeURIComponent(intent.size);
        }
        main += '<a class="nga-search__intent" data-nga-nav href="' + esc(href) + '">' +
          '<span class="nga-search__intent-icon">' + I.sparkle + '</span>' +
          '<span class="nga-search__intent-text">' +
            (intent.label ? '<span class="nga-search__intent-label">' + esc(intent.label) + '</span>' : '') +
            '<span class="nga-search__intent-title">' + esc(this.str('goTo', 'Go to')) + ' ' + esc(intent.title) +
              (intent.size ? ' &middot; ' + esc(this.str('size', 'size')) + ' ' + esc(intent.size) : '') +
            '</span></span>' + I.arrow + '</a>';
      }

      if (products.length) {
        main += '<div class="nga-search__main-head">' +
          '<h3 class="nga-search__main-title">' + esc(this.str('products', 'Products')) + '</h3>' +
          '<span class="nga-search__main-count">' + products.length + ' ' +
          esc(products.length === 1 ? this.str('result', 'result') : this.str('results', 'results')) +
          '</span></div><ul class="nga-search__cards">';

        products.slice(0, 4).forEach(function (p) {
          var stars = '';
          if (p.rating) {
            var r = Math.round(p.rating);
            for (var i = 1; i <= 5; i++) stars += (i <= r ? I.star : I.starO);
          }
          var swatches = '';
          (p.swatches || []).forEach(function (s) {
            var style = s.image
              ? 'background-image:url(' + s.image + ')'
              : 'background-color:' + (s.color || 'var(--nga-cream-deep)');
            swatches += '<span class="nga-scard__swatch" style="' + style + '" title="' + esc(s.name) + '"></span>';
          });

          main += '<li><article class="nga-scard">' +
            '<button type="button" class="nga-scard__wish" data-nga-wish="' + esc(p.url.split('/').pop().split('?')[0]) + '"' +
              ' data-nga-wish-title="' + esc(p.title) + '" data-nga-wish-url="' + esc(p.url) + '"' +
              ' data-nga-wish-image="' + esc(p.image || '') + '" data-nga-wish-price="' + p.price + '"' +
              ' aria-pressed="false">' +
              '<span class="nga-icon-outline">' + I.heart + '</span>' +
              '<span class="nga-icon-filled">' + I.heartFull + '</span>' +
            '</button>' +
            '<a class="nga-scard__link" data-nga-nav href="' + esc(p.url) + '">' +
              '<span class="nga-scard__media">' +
                (p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy" decoding="async">' : '') +
              '</span>' +
              '<span class="nga-scard__body">' +
                '<span class="nga-scard__title">' + highlight(p.title, q) + '</span>' +
                (p.subtitle ? '<span class="nga-scard__sub">' + esc(p.subtitle) + '</span>' : '') +
                (stars ? '<span class="nga-scard__rating"><span class="nga-scard__stars">' + stars + '</span>' +
                  (p.rating_count ? '<span>(' + p.rating_count + ')</span>' : '') + '</span>' : '') +
                '<span class="nga-scard__price">' + esc(money(p.price)) +
                  (p.compare_at_price && p.compare_at_price > p.price ? '<s>' + esc(money(p.compare_at_price)) + '</s>' : '') +
                '</span>' +
                (swatches ? '<span class="nga-scard__swatches">' + swatches + '</span>' : '') +
              '</span>' +
            '</a>' +
            (p.available && p.variant_id
              ? '<button type="button" class="nga-scard__quick" data-nga-search-add="' + p.variant_id + '">' +
                I.bag + '<span>' + esc(self.str('quickAdd', 'Quick add')) + '</span></button>'
              : '') +
          '</article></li>';
        });

        main += '</ul>';
      } else if (!intent) {
        main += '<div class="nga-search__empty">' +
          '<h3 class="nga-search__empty-title">' + esc(this.str('noMatch', 'No match for')) +
            ' &ldquo;' + esc(q) + '&rdquo;</h3>' +
          '<p class="nga-search__empty-text">' + esc(this.str('noMatchHelp', '')) + '</p></div>';
      }

      /* ---- Footer bar ---- */
      var bar =
        '<div class="nga-search__bar">' +
          '<span class="nga-search__hint">' + esc(this.str('hintPress', 'Press')) +
            ' <span class="nga-search__key">&uarr;</span><span class="nga-search__key">&darr;</span> ' +
            esc(this.str('hintNavigate', 'to navigate')) + ' &middot; ' +
            '<span class="nga-search__key">&crarr;</span> ' + esc(this.str('hintSelect', 'to select')) +
          '</span>' +
          '<a class="nga-search__viewall" data-nga-nav href="/search?q=' + encodeURIComponent(q) + '">' +
            esc(this.str('viewAll', 'View all')) + ' ' + total + ' ' +
            esc(total === 1 ? this.str('result', 'result') : this.str('results', 'results')) + ' ' +
            esc(this.str('for', 'for')) + ' &lsquo;' + esc(q) + '&rsquo;' + I.arrow +
          '</a>' +
          (this.str('helpText', '')
            ? '<span class="nga-search__help">' + esc(this.str('helpText', '')) +
              ' <a href="' + esc(this.str('helpUrl', '/pages/contact-us')) + '">' +
              esc(this.str('helpLink', 'Chat with us')) + '</a></span>'
            : '<span></span>') +
        '</div>';

      this.panel.innerHTML =
        '<div class="nga-search__grid">' +
          '<div class="nga-search__side">' + side + '</div>' +
          '<div class="nga-search__main">' + main + '</div>' +
          this.promoHTML() +
        '</div>' + bar;

      // Let the wishlist module paint saved state on the new buttons.
      document.dispatchEvent(new CustomEvent('nga:refresh', { detail: { root: this.panel } }));
    }
  }

  if (!customElements.get('nga-search')) customElements.define('nga-search', NgaSearch);

  /* =====================================================================
     Mobile search sheet + drawer plumbing
     ===================================================================== */
  function initShells() {
    var sheet = document.querySelector('[data-nga-msearch]');
    var drawer = document.querySelector('[data-nga-drawer]');
    var scrim = document.querySelector('[data-nga-drawer-scrim]');

    function lock(on) { document.body.classList.toggle('nga-no-scroll', on); }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-nga-msearch-open]')) {
        e.preventDefault();
        if (!sheet) return;
        sheet.classList.add('is-open');
        lock(true);
        var input = sheet.querySelector('[data-nga-search-input]');
        if (input) setTimeout(function () { input.focus(); }, 320);
      }
      if (e.target.closest('[data-nga-msearch-close]')) {
        if (sheet) sheet.classList.remove('is-open');
        lock(false);
      }
      if (e.target.closest('[data-nga-drawer-open]')) {
        e.preventDefault();
        if (drawer) drawer.classList.add('is-open');
        if (scrim) scrim.classList.add('is-open');
        lock(true);
      }
      if (e.target.closest('[data-nga-drawer-close]') || e.target === scrim) {
        if (drawer) drawer.classList.remove('is-open');
        if (scrim) scrim.classList.remove('is-open');
        lock(false);
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (sheet && sheet.classList.contains('is-open')) { sheet.classList.remove('is-open'); lock(false); }
      if (drawer && drawer.classList.contains('is-open')) {
        drawer.classList.remove('is-open');
        if (scrim) scrim.classList.remove('is-open');
        lock(false);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initShells, { once: true });
  } else {
    initShells();
  }
})();
